<template>
 <div class="px-3 py-3 pb-10 space-y-4 max-w-screen-2xl mx-auto">
    <!-- Header -->
    <div class="flex items-center justify-between gap-3">
      <div class="min-w-0">
        <h1 class="text-lg font-bold leading-tight text-gray-900 dark:text-gray-50">INT Teams</h1>
        <p class="text-xs text-gray-500 dark:text-gray-400">Browse teams and their members.</p>
      </div>
      <AddTeam @toggle-message="EventMessage"></AddTeam>
    </div>

    <div class="grid grid-cols-1 gap-3 lg:grid-cols-2">
      <!-- Team list -->
      <div class="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
        <div v-if="isloading" class="space-y-2">
          <div v-for="n in 4" :key="'tsk-' + n" class="h-16 animate-pulse rounded-xl bg-gray-100 dark:bg-gray-800"></div>
        </div>
        <div v-else>
          <EmptyState
            v-if="teams.length <= 0"
            icon="mdi-account-group-outline"
            title="No teams available"
            subtitle="Teams will appear here once added."
          />
          <div v-else class="space-y-2">
            <div
              v-for="team in teams" :key="team.id"
              class="cursor-pointer rounded-xl border border-gray-200 bg-white p-3 transition hover:border-amber-300 dark:border-gray-800 dark:bg-gray-900 dark:hover:border-amber-500/40"
              @click="viewTeamDetails(team.registered_team, team.team_name)"
            >
              <div class="flex items-center justify-between gap-2">
                <div class="flex min-w-0 items-center gap-3">
                  <img :src="require('../assets/fabicon_int.png')" alt="" class="h-9 w-9 shrink-0 rounded-full" />
                  <div class="min-w-0">
                    <p class="truncate text-sm font-bold text-gray-900 dark:text-gray-50">{{ team.team_name }}</p>
                    <p class="truncate text-xs text-gray-500 dark:text-gray-400">{{ team.team_desc }}</p>
                  </div>
                </div>
                <div class="flex shrink-0 items-center gap-0.5" @click.stop>
                  <EditTeam :team="team" @toggle-message="EventMessage"></EditTeam>
                  <button
                    type="button" aria-label="Delete team"
                    :disabled="deletingId === team.id"
                    @click.stop="confirmDelete(team)"
                    class="rounded-lg p-1.5 text-rose-500 transition hover:bg-rose-50 dark:hover:bg-rose-500/10"
                  ><v-icon size="18" :class="deletingId === team.id ? 'animate-spin' : ''">{{ deletingId === team.id ? 'mdi-loading' : 'mdi-delete-outline' }}</v-icon></button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Team details -->
      <div v-if="is_viewing_details" class="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900">
        <h2 class="text-sm font-bold text-gray-900 dark:text-gray-50">Team {{ team_name }}</h2>
        <div class="mt-3 border-t border-gray-100 pt-3 dark:border-gray-800">
          <EmptyState v-if="view_team_data.length <= 0" dense icon="mdi-account-off-outline" title="No available data here" />
          <div v-else class="space-y-2">
            <div v-for="details in view_team_data" :key="details" class="flex items-center justify-between gap-2 rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-800 dark:bg-gray-900">
              <div class="flex min-w-0 items-center gap-3">
                <span class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-100 text-xs font-bold text-amber-800 dark:bg-amber-500/20 dark:text-amber-300">{{ initials(details.fullname) }}</span>
                <div class="min-w-0">
                  <p class="truncate text-sm font-semibold text-gray-900 dark:text-gray-50">{{ details.fullname }}</p>
                  <p class="truncate text-xs text-gray-500 dark:text-gray-400">{{ details.role }}</p>
                </div>
              </div>
              <span class="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800 dark:bg-amber-500/15 dark:text-amber-300">Member</span>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="flex items-center justify-center rounded-2xl border border-dashed border-gray-300 p-8 text-center dark:border-gray-700">
        <div class="text-sm text-gray-400">
          <img :src="require('../assets/fabicon_int.png')" alt="" class="mx-auto mb-2 h-10 w-10 rounded-full opacity-60" />
          Click a team to view details
        </div>
      </div>
    </div>
    <v-dialog v-model="deleteDialog" max-width="440">
      <v-card>
        <v-card-title class="text-h6">Delete team</v-card-title>
        <v-card-text>
          Delete <strong>{{ teamToDelete && teamToDelete.team_name }}</strong>? This can't be undone.
          <v-alert
            v-if="deleteError"
            type="error"
            density="compact"
            variant="tonal"
            class="mt-3"
          >
            {{ deleteError }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text="Cancel" variant="plain" @click="deleteDialog = false"></v-btn>
          <v-btn
            color="error"
            text="Delete"
            variant="flat"
            :loading="deletingId !== null"
            @click="deleteTeam()"
          ></v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <Snackbar  :toggle-snackbar="isshow" :response-message="message"></Snackbar>
  </div>
</template>
  
  <script>
  import AddTeam from './AddTeam.vue';
  import EditTeam from './EditTeam.vue';
  import Snackbar from './Snackbar.vue';
  import axios from 'axios';
  import constant from '@/constant';
  import utils from '@/utils';
  import apiMixin from '@/mixins/apiMixin';
  import formatMixin from '@/mixins/formatMixin';
  import EmptyState from '@/components/ui/EmptyState.vue';
  export default {
    name: 'TeamPage',
    components: {
        AddTeam,
        EditTeam,
        Snackbar,
        EmptyState
    },
    mixins: [apiMixin, formatMixin],
    data() {
      return {
        constant: constant,
        isshow: false,
        message: "",
        teams: [],
        view_team_data: [],
        team_name: "",
        is_viewing_details: false,
        isloading: true,
        deleteDialog: false,
        teamToDelete: null,
        deletingId: null,
        deleteError: ""
      }
    },
    methods: {
        EventMessage(value){
            this.getTeams();
            this.isshow = true;
            this.message = value;
            console.log("snackbar from team page", this.isshow);
            setTimeout(() => {
                    this.isshow = false;
            }, 1000);
        },
        getTeams(){
           axios.get(utils._api(this.constant.get_teams),{
                headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                        'Access-Control-Allow-Origin': '*',
                        'Authorization' : `Bearer ${localStorage.getItem('token')}`
                    },
            }).then((response) => {
                this.teams = response.data.data.map((team) => ({
                id: team.id,
                team_name: team.team_name,
                team_desc: team.team_desc,
                registered_team: team.registered_team
                }));
                this.isloading = false;
                // this.teams = response.data.data;
                console.log('Teams', response.data);
            }).catch(error => {
                console.error("Error fetching teams:", error);
            });
        },
        viewTeamDetails(details,name){
            this.is_viewing_details = true;
            this.view_team_data = details;
            this.team_name = name;
            console.log("team data" + this.view_team_data);

        },
        confirmDelete(team){
            this.teamToDelete = team;
            this.deleteError = "";
            this.deleteDialog = true;
        },
        deleteTeam(){
            if (!this.teamToDelete) return;
            this.deletingId = this.teamToDelete.id;
            this.deleteError = "";

            const f = new FormData();
            f.append("id", this.teamToDelete.id);

            axios.post(utils._api(this.constant.delete_team), f, {
                headers: {
                    'Accept': 'application/json',
                    'Authorization' : `Bearer ${localStorage.getItem('token')}`
                },
            }).then((response) => {
                this.deletingId = null;
                this.deleteDialog = false;
                this.teamToDelete = null;
                this.EventMessage(response.data.message);
            }).catch((error) => {
                this.deletingId = null;
                const data = error.response && error.response.data;
                this.deleteError = (data && data.message) || "Delete failed.";
            });
        }
    },
    mounted(){
        this.getTeams();
    }
  }
  </script>
  <style scoped>
  .card-pointer{
    cursor: pointer
  }
  </style>
  